import Cookies from 'js-cookie';
import jwt_decode from 'jwt-decode';

import {
  SESSION_CORRELATION_ID,
  USER_ACCESS_TOKEN,
  USER_ID_COOKIE_NAME,
  USER_REFRESH_TOKEN,
  USER_TEAM
} from '@/shared/config/cookies.config';
import { UserRole } from '@/shared/enums/user-role.enum';
import {
  userDataCleanup,
  userDataSuccess,
  userTeamDataSuccess
} from '@/shared/redux/actions/auth.actions';
import ReduxStore from '@/shared/redux/store';
import { unregisterDeviceFromNotifications } from '@/shared/services/firebase/notifications.service';
import { UserAuthUIModel } from '@/shared/ui-models/user-auth.ui-model';

import { UserTeam } from '../../../ui-models/user-auth.ui-model';

// Flag to prevent duplicate Redux dispatch during runtime token retrieval
let isRepopulatingRedux = false;

/**
 * Reads the persisted sales team from the USER_TEAM cookie.
 * Returns an empty array when the cookie is absent or unparsable.
 * The team is not encoded in the JWT, so the cookie is the only synchronous
 * source of truth for it on reload / token refresh.
 */
function getTeamFromCookie(): UserTeam[] {
  const teamCookie = Cookies.get(USER_TEAM);
  if (teamCookie && teamCookie !== 'undefined') {
    try {
      return JSON.parse(teamCookie) as UserTeam[];
    } catch (error) {
      console.error('[AUTH] Error parsing team cookie:', error);
    }
  }
  return [];
}

/**
 * Helper to repopulate Redux store from cookies when Redux is cleared (e.g., browser sleep)
 * This is a lightweight fallback for runtime token access.
 * Full initialization happens during bootstrap via refreshUserToken()
 */
function repopulateReduxFromCookies(): void {
  // Prevent duplicate dispatch if already in progress
  if (isRepopulatingRedux) {
    console.log('[AUTH] Already repopulating Redux, skipping');
    return;
  }

  // Don't repopulate if Redux already has data (shouldn't happen, but safety check)
  const currentState = ReduxStore.getState().auth;
  if (currentState.accessToken && currentState.refreshToken) {
    console.log('[AUTH] Redux already has tokens, no need to repopulate');
    return;
  }

  console.log('[AUTH] Redux store is empty, checking cookies...');
  isRepopulatingRedux = true;

  const accessToken = Cookies.get(USER_ACCESS_TOKEN);
  const refreshToken = Cookies.get(USER_REFRESH_TOKEN);

  if (accessToken && accessToken !== 'undefined' && refreshToken && refreshToken !== 'undefined') {
    console.log('[AUTH] Found tokens in cookies, repopulating Redux...');
    try {
      const { email, userId, role, permissions, name } = jwt_decode<UserAuthUIModel>(accessToken);
      ReduxStore.dispatch(
        userDataSuccess({
          accessToken,
          refreshToken,
          tokenExpiresIn: 365,
          userId,
          email,
          role,
          permissions,
          // Restore the team from its cookie too — otherwise team-gated routes
          // (e.g. Leads) flash Unauthorized until an async refetch repopulates it.
          team: getTeamFromCookie(),
          name
        })
      );
      console.log('[AUTH] Successfully repopulated Redux from cookies');
    } catch (error) {
      console.error('[AUTH] Error repopulating Redux from cookies:', error);
    }
  } else {
    console.log('[AUTH] No tokens found in cookies');
  }

  isRepopulatingRedux = false;
}

/**
 * function to get user token from data store
 * @returns {string | undefined}
 */
export function getUserAccessToken(): string | undefined {
  const reduxToken = ReduxStore.getState().auth.accessToken;
  if (reduxToken) {
    return reduxToken;
  }

  // Fallback to cookies (e.g., after browser sleep/Redux store cleared)
  const cookieToken = Cookies.get(USER_ACCESS_TOKEN);
  if (cookieToken && cookieToken !== 'undefined') {
    // Repopulate Redux store from cookies (only once)
    repopulateReduxFromCookies();
    return cookieToken;
  }

  return undefined;
}

export function getUserId(): string | undefined {
  const reduxUserId = ReduxStore.getState().auth.userId;
  if (reduxUserId) {
    return reduxUserId;
  }

  // Fallback: decode from access token in cookie
  const accessToken = Cookies.get(USER_ACCESS_TOKEN);
  if (accessToken && accessToken !== 'undefined') {
    try {
      const { userId } = jwt_decode<UserAuthUIModel>(accessToken);
      return userId;
    } catch {
      return undefined;
    }
  }

  return undefined;
}

export function getUserRefreshToken(): string | undefined {
  const cookieToken = Cookies.get(USER_REFRESH_TOKEN);
  if (cookieToken && cookieToken !== 'undefined') {
    return cookieToken;
  }

  return ReduxStore.getState().auth.refreshToken;
}

export function refreshUserToken(): UserAuthUIModel | null {
  const accessToken = Cookies.get(USER_ACCESS_TOKEN);
  const refreshToken = Cookies.get(USER_REFRESH_TOKEN);
  const salesTeamsCookie = Cookies.get(USER_TEAM);
  const salesUserTeam =
    salesTeamsCookie && salesTeamsCookie !== 'undefined' ? JSON.parse(salesTeamsCookie) : [];
  if (accessToken && accessToken !== 'undefined' && refreshToken && refreshToken !== 'undefined') {
    const { email, userId, role, permissions, name } = jwt_decode<UserAuthUIModel>(accessToken);
    const userData: UserAuthUIModel = {
      accessToken,
      refreshToken,
      name,
      email,
      userId,
      tokenExpiresIn: 365,
      role,
      permissions
    };
    if (salesUserTeam && salesUserTeam?.length > 0) {
      userData.team = salesUserTeam;
    }
    // ReduxStore.dispatch(registerUserSuccess(userData));
    ReduxStore.dispatch(
      userDataSuccess({
        accessToken: userData.accessToken,
        refreshToken: userData.refreshToken,
        tokenExpiresIn: userData.tokenExpiresIn,
        userId: userData.userId,
        email: userData.email,
        role: userData.role,
        permissions: userData.permissions,
        name: userData.name,
        team: userData.team
      })
    );
    return userData;
  } else {
    return null;
  }
}

/**
 * function to set user token to cookies and data store
 * @param userData
 */
export function setUserToken(userData: UserAuthUIModel): void {
  Cookies.set(USER_ACCESS_TOKEN, userData.accessToken, { expires: userData.tokenExpiresIn });
  Cookies.set(USER_REFRESH_TOKEN, userData.refreshToken, { expires: userData.tokenExpiresIn });
  if (userData?.team) {
    Cookies.set(USER_TEAM, JSON.stringify(userData.team), { expires: userData.tokenExpiresIn });
  }
  // The refresh-token response does not carry the user's team. Re-dispatching with an
  // undefined team would overwrite it in Redux and flash the Unauthorized screen on
  // team-gated routes until the async team refetch lands. Preserve the already-resolved
  // team (incoming payload → current Redux → cookie) so the guard never sees it empty.
  const reduxTeam = ReduxStore.getState().auth.team;
  const resolvedTeam =
    userData?.team && userData.team.length > 0
      ? userData.team
      : reduxTeam && reduxTeam.length > 0
      ? reduxTeam
      : getTeamFromCookie();
  try {
    const { name, permissions } = jwt_decode<UserAuthUIModel>(userData.accessToken);

    ReduxStore.dispatch(
      userDataSuccess({
        accessToken: userData.accessToken,
        refreshToken: userData.refreshToken,
        tokenExpiresIn: userData.tokenExpiresIn,
        userId: userData.userId,
        email: userData.email,
        role: userData.role,
        team: resolvedTeam,
        name,
        permissions
      })
    );
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    ReduxStore.dispatch(userDataCleanup());
  }
}

export function setUserTeam(team: UserTeam[]): void {
  if (team && team?.length > 0) {
    Cookies.set(USER_TEAM, JSON.stringify(team), { expires: 365 });
    try {
      ReduxStore.dispatch(
        userTeamDataSuccess({
          team
        })
      );
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      ReduxStore.dispatch(userDataCleanup());
    }
  }
}

export function hasAccess(allowedPermissions: string[]): boolean {
  console.log('evaluating permissions');
  const accessToken = Cookies.get(USER_ACCESS_TOKEN);
  if (accessToken && accessToken !== 'undefined') {
    const { permissions } = jwt_decode<UserAuthUIModel>(accessToken);
    if (permissions) {
      return allowedPermissions.some((item) => permissions.includes(item));
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export function hasRole(allowedRoles: UserRole[]): boolean {
  console.log('evaluating permissions');
  const accessToken = Cookies.get(USER_ACCESS_TOKEN);
  if (accessToken && accessToken !== 'undefined') {
    const { role } = jwt_decode<UserAuthUIModel>(accessToken);
    if (role) {
      return allowedRoles.filter((x) => role.includes(x)).length > 0;
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export function teamHasAccess(allowedPermissions: string[]): boolean {
  console.log('evaluating permissions');
  const salesTeamsCookie = Cookies.get(USER_TEAM);
  const salesUserTeam =
    salesTeamsCookie && salesTeamsCookie !== 'undefined' ? JSON.parse(salesTeamsCookie) : [];

  console.log('Extracted teams from cookies:', salesUserTeam);
  if (salesUserTeam && salesUserTeam?.length > 0) {
    return salesUserTeam?.some((t: UserTeam) => allowedPermissions?.indexOf(t.name) > -1);
  } else {
    return false;
  }
}

/**
 * function to clean up user data from cookies and data store
 */
export function cleanupUserData() {
  // Unregister from push notifications on logout
  try {
    unregisterDeviceFromNotifications();
  } catch (error) {
    console.error('Failed to unregister from notifications:', error);
  }
  ReduxStore.dispatch(userDataCleanup());
  Cookies.remove(USER_ACCESS_TOKEN);
  Cookies.remove(USER_REFRESH_TOKEN);
  Cookies.remove(USER_ID_COOKIE_NAME);
}

export function setSessionCorrelationId(correlationId: string) {
  sessionStorage.setItem(SESSION_CORRELATION_ID, correlationId);
}

export function getSessionCorrelationId(): string {
  return sessionStorage.getItem(SESSION_CORRELATION_ID) || '';
}

export function isUserSalesTeam(accessTokenToEvaluate?: string): boolean {
  console.log('evaluating permissions');
  const accessToken = accessTokenToEvaluate || Cookies.get(USER_ACCESS_TOKEN);
  if (accessToken && accessToken !== 'undefined') {
    const { role } = jwt_decode<UserAuthUIModel>(accessToken);
    if (role) {
      return (
        [
          UserRole.SALES_AGENT,
          UserRole.SALES_MANAGER,
          UserRole.E_COMMERCE_MANAGER,
          UserRole.E_COMMERCE_AUTO
        ].filter((x) => role.includes(x)).length > 0
      );
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export function isUserInsuranceTeam(accessTokenToEvaluate?: string): boolean {
  const accessToken = accessTokenToEvaluate || Cookies.get(USER_ACCESS_TOKEN);
  if (accessToken && accessToken !== 'undefined') {
    const { role } = jwt_decode<UserAuthUIModel>(accessToken);
    if (role) {
      return (
        [UserRole.INSURANCE_MANAGER, UserRole.INSURANCE_AGENT].filter((x) => role.includes(x))
          .length > 0
      );
    }
    return false;
  }
  return false;
}

export function requiresTeamMembershipForRoutes(accessTokenToEvaluate?: string): boolean {
  return (
    isUserSalesTeam(accessTokenToEvaluate) || isUserInsuranceTeam(accessTokenToEvaluate)
  );
}

export function shouldFetchTeamMembership(accessTokenToEvaluate?: string): boolean {
  return requiresTeamMembershipForRoutes(accessTokenToEvaluate);
}
